import { supabase as centralSupabase } from './supabase/supabaseClient.js'

export const getCompanies = async (supabaseClient) => {
  try {
    const client = supabaseClient || centralSupabase;
    const { data, error } = await client
      .from('companias')
      .select(`
        *,
        producto:producto(id, nombre, yoolbrand, descripcion, activo,
          planes:planes(id, nombre, descripcion, uf_base, activo, codigo_plan)
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error al obtener compañías:', error);
      throw error;
    }

    // Agregar logging para verificar los IDs
    console.log('getCompanies - datos obtenidos:', data?.length || 0, 'compañías');
    if (data) {
      data.forEach((company, index) => {
        if (!company.id || typeof company.id !== 'string') {
          console.error(`❌ getCompanies - Compañía ${index} sin ID válido:`, company);
        }
      });
    }

    return data || [];
  } catch (error) {
    console.error('Error en getCompanies:', error);
    throw error;
  }
};

/**
 * Métricas de la lista de compañías: productos y planes activos vs total, más el
 * embudo cotización → contratación → certificado. UNA consulta para todas las
 * compañías, no una por fila.
 *
 * La fuente es la vista `company_dashboard_metrics`, que ya distingue activo de
 * archivado (`activo = true AND archivado IS NOT TRUE`), cosa que contar
 * `producto.length` en el cliente no hacía.
 *
 * OJO con dos columnas de esa vista que NO se usan aquí:
 *   · `total_payment_amount` suma `pagos.amount`, que está en UF (`moneda='UF'`,
 *     `monto_cobrado_clp` viene NULL). Formatearla como CLP daba una cifra falsa.
 *   · `successful_payments` cuenta `status='success'`, valor que `pagos` nunca
 *     escribe (usa AUTHORIZED/completed/pending), así que siempre es 0.
 * La plata recaudada sale de getRecaudacionPorCompania().
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} [supabaseClient]
 * @returns {Promise<Map<string, {productos_total:number, productos_activos:number,
 *   planes_total:number, planes_activos:number, cotizaciones:number,
 *   contrataciones:number, certificados:number}>>} indexado por id de compañía.
 */
export const getCompaniesMetrics = async (supabaseClient) => {
  const client = supabaseClient || centralSupabase;
  const { data, error } = await client
    .from('company_dashboard_metrics')
    .select(
      'company_id,total_products,active_products,total_plans,active_plans,' +
      'total_cotizaciones,total_contrataciones,total_certificados',
    );

  if (error) {
    console.error('Error al obtener métricas de compañías:', error);
    throw error;
  }

  const porCompania = new Map();
  for (const m of data || []) {
    porCompania.set(m.company_id, {
      productos_total:   m.total_products ?? 0,
      productos_activos: m.active_products ?? 0,
      planes_total:      m.total_plans ?? 0,
      planes_activos:    m.active_plans ?? 0,
      cotizaciones:      m.total_cotizaciones ?? 0,
      contrataciones:    m.total_contrataciones ?? 0,
      certificados:      m.total_certificados ?? 0,
    });
  }
  return porCompania;
};

/** Tamaño de página al recorrer los cargos. Ver el bucle de abajo. */
const PAGINA_CARGOS = 1000;
/** Techo de páginas: un backstop, no un límite esperado. Ver el bucle. */
const MAX_PAGINAS_CARGOS = 50;

/**
 * Lo cobrado en CLP por compañía, con la plata de Yool separada de la de pago
 * propio. Suma `amount_paid` de las cuotas en `paid`.
 *
 * POR QUÉ `amount_paid` Y NO `amount`
 * -----------------------------------
 * `amount` es la prima en UF. `amount_paid` es lo que el PSP cobró en pesos, y es
 * el mismo campo que la cadena outbox → `finance.movements` usa para el asiento
 * `payout_bruto`. O sea: esta cifra es la misma que muestra el cierre de YoolPay,
 * leída en una consulta en vez de N llamadas al servicio payment.
 *
 * POR QUÉ SE LEE LA VISTA Y NO LA TABLA
 * -------------------------------------
 * Dos razones, y ninguna es de estilo:
 *   · `subscription_charges.compania_id` viene NULL en las 348 filas de
 *     producción. El camino poblado es cargo → suscripción → compañía, que es el
 *     que la vista implementa.
 *   · Hacer ese join desde el browser (`subscriptions!inner(company_id)`) da $0 en
 *     producción: ahí `subscriptions` tiene RLS habilitado y CERO policies, así
 *     que `authenticated` lee 0 filas y el inner join se come todo sin error. La
 *     vista es owner `postgres` y sin `security_invoker`, así que no la afecta.
 *
 * POR QUÉ PAGO PROPIO NO SE SUMA CON EL RESTO
 * -------------------------------------------
 * La cuota con `es_pago_propio` la cobró la compañía en su propia cuenta: Yool
 * nunca la recauda ni la gira (`dispatchPayouts` solo toma `pendiente`). Meterla
 * en el mismo total infla lo recaudado por Yool con plata que nunca pasó por aquí.
 * En dev, YOOL DEMO 1 tiene las dos cosas a la vez —$83.732 vía Yool y $236.079
 * de pago propio— así que la separación no es teórica.
 *
 * `cuotas_sin_monto` tampoco es cosmético: en producción 35 de 43 cuotas pagadas
 * no traen `amount_paid`, así que el total es parcial y la UI tiene que poder
 * decirlo en vez de presentar la suma como si fuera todo lo cobrado.
 *
 * Agregar en el browser es razonable mientras el volumen sea de cientos de filas
 * (43 en producción, 48 en dev). Se pagina de verdad —avanzando por lo que el
 * servidor devolvió, no por el tamaño pedido— para que un `db-max-rows` menor al
 * de la página no trunque la suma en silencio. Cuando esto pase de decenas de
 * miles de cargos, el agregado tiene que bajar a una vista en la DB.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} [supabaseClient]
 * @returns {Promise<Map<string, {recaudado_clp:number, cuotas_pagadas:number,
 *   cuotas_sin_monto:number, propio_clp:number, cuotas_propias:number}>>}
 *   indexado por id de compañía. `recaudado_clp` es lo que recaudó Yool;
 *   `propio_clp` lo que cobró la compañía por su cuenta.
 */
export const getRecaudacionPorCompania = async (supabaseClient) => {
  const client = supabaseClient || centralSupabase;
  const porCompania = new Map();
  let desde = 0;
  let pagina = 0;

  for (; pagina < MAX_PAGINAS_CARGOS; pagina += 1) {
    const { data, error } = await client
      .from('company_subscription_charges')
      .select('id, company_id, amount_paid, es_pago_propio')
      .eq('status', 'paid')
      // Sin orden estable el paginado por rango puede repetir o saltarse filas.
      .order('id', { ascending: true })
      .range(desde, desde + PAGINA_CARGOS - 1);

    if (error) {
      console.error('Error al obtener recaudación por compañía:', error);
      throw error;
    }

    const filas = data || [];
    if (filas.length === 0) break;

    for (const cargo of filas) {
      if (!cargo.company_id) continue;

      const acc = porCompania.get(cargo.company_id) || {
        recaudado_clp: 0, cuotas_pagadas: 0, cuotas_sin_monto: 0,
        propio_clp: 0, cuotas_propias: 0,
      };

      if (cargo.es_pago_propio) {
        acc.cuotas_propias += 1;
        acc.propio_clp += Number(cargo.amount_paid ?? 0);
      } else {
        acc.cuotas_pagadas += 1;
        if (cargo.amount_paid == null) acc.cuotas_sin_monto += 1;
        else acc.recaudado_clp += Number(cargo.amount_paid);
      }

      porCompania.set(cargo.company_id, acc);
    }

    desde += filas.length;
  }

  if (pagina >= MAX_PAGINAS_CARGOS) {
    // Nada de truncar callado: si se llegó aquí la suma está incompleta.
    console.warn(
      `getRecaudacionPorCompania: se cortó en ${MAX_PAGINAS_CARGOS} páginas ` +
      `(${desde} cargos). La recaudación mostrada está incompleta — el agregado ` +
      'tiene que moverse a una vista en la DB.',
    );
  }

  return porCompania;
};

export const getCompanyById = async (supabaseClient, id) => {
  try {
    const client = supabaseClient || centralSupabase;
    const { data, error } = await client
      .from('companias')
      .select(`
        *,
        producto:producto(id, nombre, yoolbrand, descripcion, activo,
          planes:planes(id, nombre, descripcion, uf_base, activo, codigo_plan)
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error al obtener compañía:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error en getCompanyById:', error);
    throw error;
  }
};

export const createCompany = async (supabaseClient, companyData) => {
  try {
    const client = supabaseClient || centralSupabase;
    const { data, error } = await client
      .from('companias')
      .insert([companyData])
      .select()
      .single();

    if (error) {
      console.error('Error al crear compañía:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error en createCompany:', error);
    throw error;
  }
};

export const updateCompany = async (supabaseClient, id, companyData) => {
  try {
    const client = supabaseClient || centralSupabase;
    const { data, error } = await client
      .from('companias')
      .update(companyData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error al actualizar compañía:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error en updateCompany:', error);
    throw error;
  }
};

export const deleteCompany = async (supabaseClient, id) => {
  try {
    const client = supabaseClient || centralSupabase;
    const { error } = await client
      .from('companias')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error al eliminar compañía:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error en deleteCompany:', error);
    throw error;
  }
};